import { Component, Host, Prop, h, Element, Event, EventEmitter, State, Listen, Watch, Method } from '@stencil/core';
import { addClass, inheritAttributes, isEmpty } from '../../utils/helpers/helpers';
import { registryLocaleStringsObserver, disconnectLocaleStringObserver } from '../../utils/i18n/i18n';

@Component({
  tag: 'lgr-input-select',
  styleUrl: 'inputSelect.scss',
  shadow: true,
})
export class InputSelect {
  @Element() el!: HTMLLgrInputSelectElement;
  /**
   * Disabled state.
   */
  @Prop({ reflect: true }) disabled: boolean = false;
  /**
   * Optional field.
   */
  @Prop({ reflect: true }) optional: boolean = false;
  /**
   * Inverse or not.
   */
  @Prop({ reflect: true }) inverse: boolean = false;
  /**
   * Status: error or success.
   */
  @Prop({ reflect: true }) status?: 'error' | 'success';
  /**
   * The label text for the select field.
   */
  @Prop({ reflect: true }) label?: string;
  /**
   * The helper text displayed below the select field.
   */
  @Prop({ reflect: true }) helperText?: string;
  /**
   * The placeholder text for the select field.
   */
  @Prop({ reflect: true }) placeholder?: string;
  /**
   * Time to clear keyboard actions.
   */
  @Prop({ reflect: true }) timeRefresh: number = 2000;
  /**
   * Auto select when keyboard navigation.
   */
  @Prop({ reflect: true }) autoSelect: boolean = true;
  /**
   * Auto select when keyboard navigation.
   */
  @Prop({ reflect: true }) largeList: boolean = true;
  /**
   * The current value of the select field.
   */
  @Prop({ reflect: true, mutable: true }) value?: string;
  /**
   * Hide the select field.
   */
  @Prop() none?: boolean = false;
  /**
   *
   */
  @Prop() dark?: boolean = false;
  /**
   * Se verdadeiro, a lista de opções não terá scroll.
   */
  @Prop({ reflect: true }) noScroll: boolean = false;
  /**
   * Event emitted with the new selected item's value every time there is a change on the selected item.
   */
  @Event({ bubbles: true, composed: true }) lgrSelectValueChanged!: EventEmitter<string>;
  /**
   *
   */
  @Event({ bubbles: true, composed: true }) lgrSelectEnterKey!: EventEmitter<string>;
  /**
   *
   */
  @Event({ bubbles: true, composed: true }) lgrSelectClicked!: EventEmitter<string>;
  /**
   *
   */
  @Method()
  async toggleDropdown () {
    this.isOpen = !this.isOpen;
  };
  /**
   *
   */
  @Method()
  async getOptions () {
    this.inheritedAttributes = inheritAttributes(this.el, ['aria-describedby', 'aria-labelledby', 'aria-label', 'form', 'name']);
    this.options = [...this.el.querySelectorAll('lgr-select-option')] as HTMLLgrSelectOptionElement[];

    const selectedOptions = this.options.filter(el => {
      return el.selected;
    });

    const lastSelectedOption = selectedOptions.pop();
    if (lastSelectedOption !== undefined) {
      this.value = lastSelectedOption.value;
    }
  }

  @State() locale: any;
  private localeObserver: MutationObserver | undefined;

  @State() isOpen = false;
  @State() selectedOption: HTMLLgrSelectOptionElement | undefined;

  private inheritedAttributes: { [key: string]: string } = {};

  private options: HTMLLgrSelectOptionElement[] = [];
  private focusedIndex = -1;

  private customSelect!: HTMLElement;
  private searchTerm = '';

  @Listen('lgrSelectOptionClicked', { target: 'body' })
  lgrSelectOptionClickedHandler(ev: any) {
    if (!this.options.includes(ev.target)) return;
    this.value = ev.detail;
  }

  @Watch('value')
  watchValueHandler(newValue: string) {
    this.selectedOption = this.options.find(el => el.value === newValue) as HTMLLgrSelectOptionElement;
    this.focusedIndex = this.options.indexOf(this.selectedOption);
    this.lgrSelectValueChanged.emit(newValue);
  }

  private updateSelection() {
    const selected = this.options[this.focusedIndex];
    this.value = selected.value;
    this.selectedOption?.scrollIntoView({ block: 'nearest' });
  }

  private handleKeyDown = (event: KeyboardEvent) => {
    let debounceTimeout;

    switch (event.key) {
      case ' ':
        event.preventDefault();
        this.isOpen = true;
        break;
      case 'Enter':
        event.preventDefault();
        this.lgrSelectEnterKey.emit(this.value);
        this.isOpen = !this.isOpen;
        break;
      case 'Escape':
        event.preventDefault();
        this.isOpen = false;
        break;
      case 'Tab':
        if (this.isOpen) {
          event.preventDefault();
          this.isOpen = false;
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.focusNextOption();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusPreviousOption();
        break;
      default:
        if (/^[a-zA-Z0-9]$/.test(event.key)) {


          event.preventDefault();
          clearTimeout(debounceTimeout);
          this.searchTerm += event.key.toLowerCase();

          debounceTimeout = setTimeout(() => {
            this.searchTerm = '';

          }, this.timeRefresh);

          const searchedOption = this.options.find(option => {
            return option.innerText.toLowerCase().startsWith(this.searchTerm);
          });

          if (searchedOption) {
            this.value = searchedOption.value;
            this.selectedOption?.scrollIntoView({ block: 'nearest' });
          }
        }
    }
  };

  private onBlur = () => {
    if (this.isOpen) {
      this.isOpen = false;
      this.customSelect.focus();
    }
  };

  private focusNextOption() {
    this.focusedIndex = (this.focusedIndex + 1) % this.options.length;
    this.updateSelection();
  }

  private focusPreviousOption() {
    this.focusedIndex = (this.focusedIndex - 1 + this.options.length) % this.options.length;
    this.updateSelection();
  }

  async connectedCallback() {
    this.localeObserver = await registryLocaleStringsObserver((localeStrings: any) => (this.locale = localeStrings));
  }

  disconnectedCallback() {
    disconnectLocaleStringObserver(this.localeObserver);
  }

  async componentWillLoad(): Promise<void> {
    this.getOptions();
  }

  render() {
    const labelId = !isEmpty(this.el.id) ? `${this.el.id}-select-label` : undefined;
    const helperTextId = !isEmpty(this.el.id) ? `${this.el.id}-helper-text` : undefined;
    const optionalText = this.optional ? ` (${this.locale?.optional})` : '';

    let filteredOptions: Element[] = [];
    if (this.options.length > 0) {
      const [first, ...rest] = this.options;
      if (
        first.innerText.trim() === '- Nenhum -' ||
        first.value === '' ||
        first.value === undefined ||
        first.value === null
      ) {
        filteredOptions = rest;
      } else {
        filteredOptions = this.options;
      }
    }

    return (
      <Host style={{ pointerEvents: this.disabled ? 'none' : undefined }} aria-disabled={this.disabled ? 'true' : null}>
        {this.label && (
          <label id={labelId} class={`${addClass([this.disabled ? 'disabled' : null, this.inverse ? 'inverse' : null])}`}>
            {this.label}
            {optionalText}
          </label>
        )}

        <div
          class={`${addClass([
            'lgr-input-field',
            'custom-select',
            this.disabled ? 'disabled' : null,
            this.inverse ? 'inverse' : null,
            isEmpty(this.status) ? null : this.status,
            this.none ? 'none' : null,
            this.dark ? 'dark' : null
          ])}`}
          role="combobox"
          aria-expanded={this.isOpen}
          aria-labelledby={isEmpty(this.el.id) ? undefined : `${labelId} ${helperTextId}`}
          aria-role="combobox"
          aria-required={(!this.optional).toString()}
          onKeyDown={this.handleKeyDown}
          onClick={() => this.toggleDropdown()}
          onBlur={this.onBlur}
          tabindex={this.disabled ? '-1' : '0'}
          ref={el => {
            this.customSelect = el as HTMLDivElement;
          }}
          {...this.inheritedAttributes}
        >
          <p
            class={`${addClass([this.selectedOption ? '' : 'placeholder', this.inverse ? 'inverse' : null, this.disabled ? 'disabled' : ''])}`}
            aria-live="polite"
            aria-relevant="additions text"
          >
            {this.selectedOption ? this.selectedOption.innerText : this.placeholder}
          </p>
          <lgr-icon
            class={`${addClass([
              'lgr-select-expand-icon',
              this.isOpen ? 'lgr-open-list' : 'lgr-closed-list',
              this.inverse ? 'inverse' : null,
              this.status,
              this.disabled ? 'disabled' : '',
            ])}`}
          >
            expand_more
          </lgr-icon>
        </div>

        {this.isOpen && (
          <ul
            class={`options-list${this.noScroll ? ' no-scroll' : ''}`}
            role="listbox"
            aria-role="listbox"
            onClick={() => this.lgrSelectClicked.emit(this.value)}
          >
            {filteredOptions.map(option => (
              <slot name={option.getAttribute('slot') || undefined}></slot>
            ))}
          </ul>
        )}

        {this.helperText && (
          <p id={helperTextId} class={`${addClass(['lgr-input-helper-text', this.disabled ? 'disabled' : null, this.inverse ? 'inverse' : null, this.status])}`}>
            {this.helperText}
          </p>
        )}
      </Host>
    );
  }
}
